import { TextButton, IconButton } from '@creditornot/cb-button';
import { Cross } from '@creditornot/cb-icons';
import { typographyCss } from '@creditornot/cb-ingredients';
import { color } from '@creditornot/cb-ingredients/design-tokens';
import { Popover, PopoverProps } from '@creditornot/cb-popover';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import styled, { css } from 'styled-components';

import useUser from 'common/hooks/useUser';
import { useOnboardingStepsContext } from 'modules/shared-menu/contexts/OnboardingStepsContext';

import { OnboardingStepConfigKeys } from './types';
import useOnboardingGuideConfig from './useOnboardingGuideConfig';

const StyledPopover = styled(Popover)`
  max-width: 400px;
  padding: 16px;
`;

const StepContenWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const StepHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-block-end: 12px;
`;

const NumberWrapper = styled.div`
  display: flex;
  align-items: center;
`;

const NumberItem = styled.p`
  background-color: ${color.bgFillBrand};
  padding: 8px;
  color: ${color.bg};
  ${typographyCss.Title2};
  border-radius: 50%;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-block: 0;
`;

const TitleWrapper = styled.div`
  display: flex;
  align-items: center;
`;

const StepTitle = styled.h1`
  ${typographyCss.Title1()};
  margin-inline-start: 12px;
  margin-block: 0;
`;

const StyledIconButton = styled(IconButton)``;

const StepDesc = styled.p`
  ${typographyCss.Body3()};
  margin-block-start: 0;
`;

const StepFooter = styled.div`
  display: flex;
  justify-content: end;
  gap: 12px;
`;

const TargetComponentWrapper = styled.div<{ active?: boolean; isNavItem: boolean }>`
  ${({ isNavItem }) =>
    isNavItem &&
    css`
      width: calc(100% + 16px);
      margin-inline-start: -8px;
    `}

  ${({ active }) =>
    active &&
    css`
      border: 2px solid ${color.bgFillBrand};
      border-radius: 10px;
      box-sizing: border-box;
      padding: 4px;
    `}
`;

type OnboardingPopoverProps = {
  placement: PopoverProps['placement'];
  step: OnboardingStepConfigKeys;
  children: React.ReactNode;
  isNavItem?: boolean;
};

export const OnboardingPopover = ({ placement, step, children, isNavItem = false }: OnboardingPopoverProps) => {
  const onboardingGuideConfig = useOnboardingGuideConfig();
  const stepConfig = onboardingGuideConfig[step];

  const { currentOnboardingStep, updateCurrentOnboardingStep } = useOnboardingStepsContext();
  const { isEmployee } = useUser();
  const isActive = currentOnboardingStep === step && !isEmployee;

  const onPreviousClick = () => {
    if (stepConfig.firstStep) {
      updateCurrentOnboardingStep('ONBOARDING_SKIPPED');
    } else {
      if (stepConfig.previousStep) {
        updateCurrentOnboardingStep(stepConfig.previousStep);
      }
    }
  };
  const onNextClick = () => {
    if (stepConfig.lastStep) {
      updateCurrentOnboardingStep('ONBOARDING_COMPLETED');
    } else {
      updateCurrentOnboardingStep(stepConfig.nextStep);
    }
  };

  const handleCrossClick = () => {
    updateCurrentOnboardingStep('ONBOARDING_SKIPPED');
  };

  return (
    <StyledPopover
      placement={placement}
      open={isActive}
      content={
        <StepContenWrapper>
          <StepHeader>
            <TitleWrapper>
              <NumberWrapper>
                <NumberItem>{stepConfig.number}</NumberItem>
              </NumberWrapper>
              <StepTitle>{stepConfig.title}</StepTitle>
            </TitleWrapper>
            <StyledIconButton variant="neutral" size="tiny" background="transparent" onClick={handleCrossClick}>
              <Cross />
            </StyledIconButton>
          </StepHeader>
          <StepDesc>{stepConfig.description}</StepDesc>
          <StepFooter>
            <TextButton onClick={onPreviousClick} variant="grey">
              {stepConfig.firstStep ? (
                <FormattedMessage id="listing-manager.onboarding.skip-tour" />
              ) : (
                <FormattedMessage id="listing-manager.onboarding.previous-step" />
              )}
            </TextButton>
            <TextButton onClick={onNextClick}>
              {stepConfig.lastStep ? (
                <FormattedMessage id="listing-manager.onboarding.complete-onboarding" />
              ) : (
                <FormattedMessage id="listing-manager.onboarding.next-step" />
              )}
            </TextButton>
          </StepFooter>
        </StepContenWrapper>
      }
    >
      {({ refProps }) => (
        <TargetComponentWrapper {...refProps} active={isActive} isNavItem={isNavItem}>
          {children}
        </TargetComponentWrapper>
      )}
    </StyledPopover>
  );
};
